import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ForwardedRef,
} from 'react';
import { twJoin } from 'tailwind-merge';

import { TamagoshiIconComponent } from '@/core/tamagoshiTailwind/interfaces';
import { ChevronDownIcon } from '@/domains/core/tamagoshiTailwind/icons/ChevronDownIcon';
import { ChevronUpIcon } from '@/domains/core/tamagoshiTailwind/icons/ChevronUpIcon';

type BaseProps = {
  /** Indicates the activated status */
  active?: boolean;
  /** Indicates whether the button is disabled */
  disabled?: boolean;
  /** Indicates menu type with arrow icon */
  menu?: boolean;
} & Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'title'>;

type IconOnlyButtonProps = BaseProps & {
  /** Aria label for the button - Only for icon-only buttons */
  accessibilityLabel: string;
  /** Icon component to be displayed in the button */
  icon: TamagoshiIconComponent;
  /** Whether the icon should be displayed on the right or left */
  showIconRight?: never;
  /** Optional text to be displayed in the button */
  text?: never;
};

type TextButtonProps = BaseProps & {
  /** Aria label for the button - Not allowed for text buttons */
  accessibilityLabel?: never;
  /** Optional icon component to be displayed in the button */
  icon?: TamagoshiIconComponent;
  /** Whether the icon should be displayed on the right or left */
  showIconRight?: boolean;
  /** Text to be displayed in the button */
  text: string;
};

export type QuickFilterButtonProps = TextButtonProps | IconOnlyButtonProps;

export const QuickFilterButton = forwardRef<
  HTMLButtonElement,
  QuickFilterButtonProps
>(
  (
    {
      active = false,
      accessibilityLabel,
      disabled = false,
      menu = false,
      icon: Icon,
      showIconRight = false,
      text,
      ...props
    }: QuickFilterButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const MenuIcon = menu && (active ? ChevronUpIcon : ChevronDownIcon);
    return (
      <button
        aria-label={accessibilityLabel}
        className={twJoin(
          `relative flex h-[40px] items-center justify-center gap-xs
          rounded-card border bg-foundation-inverted px-s text-left text-body2
          font-semibold hover:bg-foundation-primary
          active:bg-foundation-secondary disabled:pointer-events-none`,
          showIconRight ? 'flex-row-reverse' : 'flex-row',
          active ? 'border-foundation-tertiary' : 'border-foundation-secondary',
        )}
        disabled={disabled}
        ref={ref}
        type="button"
        {...props}
      >
        {Icon && <Icon className="text h-[20px] w-[20px]" />}
        {text && <div className="text whitespace-nowrap">{text}</div>}
        {MenuIcon && (
          <MenuIcon className="text h-[16px] w-[16px]" data-icon="arrow" />
        )}
      </button>
    );
  },
);

QuickFilterButton.displayName = 'QuickFilterButton';
